import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Analytics } from '../../types';
import {
  Users,
  Activity,
  BarChart2,
  AlertCircle,
  Clock
} from 'lucide-react';

export const AnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getAnalytics();
        setAnalytics(response);
      } catch (err: any) {
        setError(err.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Analytics Dashboard</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* User Stats */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">User Statistics</h3>
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Total Users</span>
                <span className="text-white">{analytics?.users.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Users</span>
                <span className="text-white">{analytics?.users.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">New Today</span>
                <span className="text-white">{analytics?.users.new_today}</span>
              </div>
            </div>
          </div>

          {/* Challenge Stats */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Challenge Statistics</h3>
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Total Challenges</span>
                <span className="text-white">{analytics?.challenges.total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Challenges</span>
                <span className="text-white">{analytics?.challenges.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Completion Rate</span>
                <span className="text-white">{analytics?.challenges.completion_rate}%</span>
              </div>
            </div>
          </div>

          {/* System Metrics */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">System Metrics</h3>
              <BarChart2 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">CPU Usage</span>
                <span className="text-white">{analytics?.system.cpu_usage}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Memory Usage</span>
                <span className="text-white">{analytics?.system.memory_usage}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Containers</span>
                <span className="text-white">{analytics?.system.active_containers}</span>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Last Updated</h3>
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Created At</span>
                <span className="text-white">{new Date(analytics?.created_at || '').toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Updated At</span>
                <span className="text-white">{new Date(analytics?.updated_at || '').toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 